import React, { useMemo, useRef } from 'react';
import {
    Box, Typography, Paper, CircularProgress, Divider, Button,
} from '@mui/material';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useReport } from '../../contexts/ReportContext';
import {
    formatDate,
} from '../../utils/financialCalculations';
import { reportConfigs } from '../../reports/reportConfigs';

function ReportView({ reportData: reportDataProp }) {
    const { reportData: reportDataFromContext, loading: contextLoading } = useReport();
    const reportData = reportDataProp || reportDataFromContext;
    const loading = !reportDataProp && contextLoading;
    const reportContentRef = useRef(null);

    const reportMetaInfo = useMemo(() => {
        if (!reportData) return '';
        const parts = [];
        if (reportData.createdAt) {
            parts.push(`Created: ${formatDate(reportData.createdAt)}`);
        }
        if (reportData.valuationDate) {
            parts.push(`Valuation Date: ${formatDate(reportData.valuationDate)}`);
        }
        if (reportData.updatedAt) {
            parts.push(`Updated: ${formatDate(reportData.updatedAt)}`);
        }
        return parts.join(' | ');
    }, [reportData]);

    if (loading) {
        return <CircularProgress />;
    }

    if (!reportData || Object.keys(reportData).length === 0 || !reportData.reportType) {
        return <Typography>Report data is not available.</Typography>;
    }

    const config = reportConfigs[reportData.reportType];

    const handleDownloadPdf = async () => {
        try {
            const pdf = new jsPDF({
                orientation: 'p',
                unit: 'mm',
                format: 'a4',
            });
            let y = 15;
            pdf.setFontSize(18);
            pdf.setFont('helvetica', 'bold');
            pdf.text(config.name, 105, y, { align: 'center' });
            y += 10;
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            if (reportMetaInfo) {
                pdf.text(reportMetaInfo, 105, y, { align: 'center' });
                y += 8;
            }
            pdf.setDrawColor(150);
            pdf.line(15, y, 195, y);
            y += 6;

            for (const section of config.reportSections) {
                if (section.component.addPdfContent) {
                    y = section.component.addPdfContent(pdf, reportData, y);
                }
            }
            
            pdf.save(`${config.id}-report.pdf`);
        } catch (error) {
            console.error("Failed to generate PDF. See console for details.", error);
        }
    };

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, mt: 1 }}>
                <Button variant="contained" onClick={handleDownloadPdf}>
                    Download PDF
                </Button>
            </Box>
            <Paper ref={reportContentRef} elevation={3} sx={{ p: 3, mt: 1, lineHeight: '1.6' }}>
                <Typography variant="h4" gutterBottom align="center">
                    {config.name} Report
                </Typography>
                <Typography variant="subtitle1" align="center" gutterBottom>
                    {reportMetaInfo}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {config.reportSections.map(section => {
                    const SectionComponent = section.component;
                    return <SectionComponent key={section.id} reportData={reportData} />;
                })}
            </Paper>
        </>
    );
}

export default ReportView;
